import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  Wallet,
  TrendingUp,
  AlertCircle,
  Search,
  Store,
  ChevronDown,
  ChevronUp,
  Clock,
  RefreshCw,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { getCashiersOverview, type CashierBalance, type CashiersOverviewResponse } from "@/lib/api";
import { formatMajor, formatRelative } from "@/lib/format";
import {
  formatCashierName,
  cashierInitials,
  cashierSubtitle,
} from "@/lib/cashierName";

type Currency = "CDF" | "USD";
type StatusFilter = "all" | "active" | "inactive";
type ActivityFilter = "all" | "dormant" | "active_24h";
type SortKey = "balance" | "share" | "name" | "last_tx";

const METHODS = ["mpesa", "orange", "airtel", "afrimoney"] as const;
const METHOD_COLORS: Record<string, string> = {
  mpesa: "#00A551",
  orange: "#FF7A00",
  airtel: "#E4002B",
  afrimoney: "#F5B300",
};

function isDormant(c: CashierBalance): boolean {
  if (!c.is_active) return false;
  if (!c.last_transaction_at) return true;
  return Date.now() - new Date(c.last_transaction_at).getTime() > 86_400_000;
}

// ---------------------------------------------------------------------------

export function CashiersBalancesPage() {
  const auth = useAuth();
  const merchantId = auth.status === "authenticated" ? auth.user.merchant_id : null;

  const [currency, setCurrency] = useState<Currency>("CDF");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [activity, setActivity] = useState<ActivityFilter>("all");
  const [sortBy, setSortBy] = useState<SortKey>("balance");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const query = useQuery<CashiersOverviewResponse>({
    queryKey: ["cashiers-overview", merchantId],
    queryFn: getCashiersOverview,
    enabled: !!merchantId,
  });

  const data = query.data ?? null;
  const cashiers = data?.cashiers ?? [];
  const totals = data?.totals_by_currency ?? { CDF: 0, USD: 0 };
  const counts = data?.counts ?? { total: 0, active: 0, with_balance: 0 };
  const dormantCount = cashiers.filter(isDormant).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = cashiers.filter((c) => {
      if (q) {
        const display = formatCashierName(c).toLowerCase();
        const hit =
          display.includes(q) ||
          c.firstname.toLowerCase().includes(q) ||
          c.lastname.toLowerCase().includes(q) ||
          c.username.toLowerCase().includes(q) ||
          c.phone_number.toLowerCase().includes(q) ||
          (c.store_name || "").toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (status === "active" && !c.is_active) return false;
      if (status === "inactive" && c.is_active) return false;
      if (activity === "dormant" && !isDormant(c)) return false;
      if (activity === "active_24h") {
        if (!c.last_transaction_at) return false;
        const days = (Date.now() - new Date(c.last_transaction_at).getTime()) / 86_400_000;
        if (days > 1) return false;
      }
      return true;
    });
    return [...list].sort((a, b) => {
      switch (sortBy) {
        case "balance":
          return (b.totals[currency] ?? 0) - (a.totals[currency] ?? 0);
        case "share":
          return (b.share_pct[currency] ?? 0) - (a.share_pct[currency] ?? 0);
        case "name":
          return formatCashierName(a).localeCompare(formatCashierName(b));
        case "last_tx": {
          const ta = a.last_transaction_at ? new Date(a.last_transaction_at).getTime() : 0;
          const tb = b.last_transaction_at ? new Date(b.last_transaction_at).getTime() : 0;
          return tb - ta;
        }
        default:
          return 0;
      }
    });
  }, [cashiers, search, status, activity, sortBy, currency]);

  const shownTotal = filtered.reduce((s, c) => s + Number(c.totals[currency] ?? 0), 0);

  const toggleExpand = (cid: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  return (
    <div>
      <PageHeader
        title="Soldes des caissiers"
        breadcrumbs={[{ label: "Mon commerce" }, { label: "Soldes caissiers" }]}
        actions={
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="btn btn-light btn-sm"
          >
            <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
            Actualiser
          </button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard
          label="Total caissiers"
          value={counts.total.toLocaleString("fr-FR")}
          sub={`${counts.active} actif${counts.active > 1 ? "s" : ""}`}
          icon={<Users size={20} />}
          color="primary"
        />
        <StatCard
          label={`Solde total ${currency}`}
          value={formatMajor(Number(totals[currency] ?? 0), currency)}
          sub="Sous responsabilité caissiers"
          icon={<Wallet size={20} />}
          color="success"
        />
        <StatCard
          label="Avec solde"
          value={counts.with_balance.toLocaleString("fr-FR")}
          sub="ont du cash actuellement"
          icon={<TrendingUp size={20} />}
          color="info"
        />
        <StatCard
          label="Dormants >24h"
          value={dormantCount.toLocaleString("fr-FR")}
          sub="sans transaction récente"
          icon={<AlertCircle size={20} />}
          color={dormantCount > 0 ? "warning" : "primary"}
        />
      </div>

      {/* Toolbar */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher un caissier, magasin, téléphone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-input pl-10 py-2 text-sm w-full"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="inline-flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
                {(["CDF", "USD"] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCurrency(c)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      currency === c
                        ? "bg-white text-brand-700 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as StatusFilter)}
                className="form-input py-2 px-3 text-sm w-auto"
              >
                <option value="all">Tous statuts</option>
                <option value="active">Actifs</option>
                <option value="inactive">Inactifs</option>
              </select>
              <select
                value={activity}
                onChange={(e) => setActivity(e.target.value as ActivityFilter)}
                className="form-input py-2 px-3 text-sm w-auto"
              >
                <option value="all">Toute activité</option>
                <option value="active_24h">Actifs 24h</option>
                <option value="dormant">Dormants &gt;24h</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortKey)}
                className="form-input py-2 px-3 text-sm w-auto"
              >
                <option value="balance">Solde décroissant</option>
                <option value="share">Part décroissante</option>
                <option value="name">Nom A→Z</option>
                <option value="last_tx">Dernière transaction</option>
              </select>
            </div>
          </div>
          {(search || status !== "all" || activity !== "all") && (
            <div className="mt-3 text-xs text-slate-500">
              {filtered.length} caissier{filtered.length > 1 ? "s" : ""} affiché
              {filtered.length > 1 ? "s" : ""} · total filtré :{" "}
              <span className="font-semibold text-[#181c32]">
                {formatMajor(shownTotal, currency)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* List */}
      {query.isLoading ? (
        <div className="card card-body text-[#7e8299]">Chargement…</div>
      ) : query.isError ? (
        <div className="card card-body text-danger-700">
          Erreur au chargement des soldes caissiers.
        </div>
      ) : filtered.length === 0 ? (
        <div className="card card-body text-center text-slate-500 py-12">
          Aucun caissier ne correspond aux filtres.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => {
            const isOpen = expanded.has(c.cashier_id);
            const balance = Number(c.totals[currency] ?? 0);
            const share = Number(c.share_pct[currency] ?? 0);
            const dormant = isDormant(c);
            return (
              <div
                key={c.cashier_id}
                id={`cashier-${c.cashier_id}`}
                className="card overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(c.cashier_id)}
                  className="w-full px-4 sm:px-5 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="w-11 h-11 rounded-xl bg-brand-500 text-white font-bold flex items-center justify-center shrink-0">
                    {cashierInitials(c)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-[#181c32]">{formatCashierName(c)}</div>
                      {!c.is_active && (
                        <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                          Inactif
                        </span>
                      )}
                      {dormant && c.is_active && (
                        <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-warning-50 text-warning-700 inline-flex items-center gap-1">
                          <Clock size={9} />
                          Dormant
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1.5">
                      <Store size={11} className="text-slate-400" />
                      {cashierSubtitle(c)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-[#181c32] tabular-nums">
                      {formatMajor(balance, currency)}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {share.toFixed(1)}% du total ·{" "}
                      {c.last_transaction_at ? formatRelative(c.last_transaction_at) : "jamais"}
                    </div>
                  </div>
                  <div className="text-slate-400 shrink-0">
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 bg-[#fcfcfd] px-4 sm:px-5 py-4">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">
                      Répartition par méthode ({currency})
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {METHODS.map((m) => {
                        const v = Number(c.by_method?.[m]?.[currency] ?? 0);
                        return (
                          <div
                            key={m}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ background: METHOD_COLORS[m] }}
                              />
                              <span className="text-xs font-semibold capitalize text-slate-600">
                                {m}
                              </span>
                            </div>
                            <div className="text-sm font-bold text-[#181c32] tabular-nums">
                              {formatMajor(v, currency)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                      <MetaRow
                        label="Solde CDF"
                        value={formatMajor(Number(c.totals.CDF ?? 0), "CDF")}
                      />
                      <MetaRow
                        label="Solde USD"
                        value={formatMajor(Number(c.totals.USD ?? 0), "USD")}
                      />
                      <MetaRow label="Téléphone" value={c.phone_number || "—"} mono />
                      <MetaRow label="Magasin" value={c.store_name || "—"} />
                      <MetaRow
                        label="Dernière tx"
                        value={
                          c.last_transaction_at ? formatRelative(c.last_transaction_at) : "—"
                        }
                      />
                      <MetaRow
                        label="Dernier login"
                        value={c.last_login_at ? formatRelative(c.last_login_at) : "—"}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  sub,
  icon,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  color: "primary" | "success" | "info" | "warning";
}) {
  const cls: Record<typeof color, string> = {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    info: "bg-info-50 text-info-700",
    warning: "bg-warning-50 text-warning-700",
  };
  return (
    <div className="card card-body">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
          <div className="text-2xl font-bold text-[#181c32] mt-2 truncate">{value}</div>
          {sub && <div className="text-xs text-[#7e8299] mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${cls[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div
        className={`text-[#181c32] ${mono ? "font-mono text-xs" : "text-sm"} font-medium truncate mt-0.5`}
      >
        {value}
      </div>
    </div>
  );
}
